import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { DetailPageLayout } from '../../../components/layouts';
import { usePageTitle, useSEO } from '../../../hooks/shared';
import { civicAPI } from '../../elections/api/civic';
import PlaceMap from '../components/PlaceMap';

// Map committee names (as they appear from house.gov) to their website URLs
const COMMITTEE_URLS = {
  Agriculture: 'https://agriculture.house.gov',
  Appropriations: 'https://appropriations.house.gov',
  'Armed Services': 'https://armedservices.house.gov',
  Budget: 'https://budget.house.gov',
  'Education and Workforce': 'https://edworkforce.house.gov',
  'Education and the Workforce': 'https://edworkforce.house.gov',
  'Energy and Commerce': 'https://energycommerce.house.gov',
  Ethics: 'https://ethics.house.gov',
  'Financial Services': 'https://financialservices.house.gov',
  'Foreign Affairs': 'https://foreignaffairs.house.gov',
  'Homeland Security': 'https://homeland.house.gov',
  'House Administration': 'https://cha.house.gov',
  Judiciary: 'https://judiciary.house.gov',
  'Natural Resources': 'https://naturalresources.house.gov',
  'Oversight and Government Reform': 'https://oversight.house.gov',
  'Oversight and Accountability': 'https://oversight.house.gov',
  Rules: 'https://rules.house.gov',
  Science: 'https://science.house.gov',
  'Science, Space, and Technology': 'https://science.house.gov',
  'Science/Space/Technology': 'https://science.house.gov',
  'Small Business': 'https://smallbusiness.house.gov',
  'Transportation and Infrastructure': 'https://transportation.house.gov',
  'Transportation/Infrastructure': 'https://transportation.house.gov',
  "Veterans' Affairs": 'https://veterans.house.gov',
  "Veterans\\' Affairs": 'https://veterans.house.gov',
  'Ways and Means': 'https://waysandmeans.house.gov',
  Intelligence: 'https://intelligence.house.gov',
  'Select Committee on US-China Competition': 'https://chinaselectcommittee.house.gov',
  'Joint Committee on Taxation': 'https://jct.gov',
  'Library Committee': 'https://cha.house.gov/joint-committee-on-library',
};

function getCommitteeUrl(name) {
  // Try exact match first
  if (COMMITTEE_URLS[name]) return COMMITTEE_URLS[name];
  // Try case-insensitive partial match
  const lower = name.toLowerCase();
  for (const [key, url] of Object.entries(COMMITTEE_URLS)) {
    if (
      key.toLowerCase() === lower ||
      lower.includes(key.toLowerCase()) ||
      key.toLowerCase().includes(lower)
    ) {
      return url;
    }
  }
  return null;
}

const DISTRICT_CONFIG = {
  congressional: {
    title: 'Congressional District',
    color: '#EF4444',
    about:
      "Your U.S. Representative serves in the House of Representatives in Washington, D.C. They vote on federal legislation — the budget, taxes, immigration, defense spending, civil rights, and healthcare policy like the Affordable Care Act. They also control the federal purse: every dollar the government spends must be approved by the House. Congressional representatives serve 2-year terms, meaning they face voters frequently and tend to be highly responsive to constituent pressure. A single phone call to your rep's office carries more weight than you might think.",
    powers: [
      'Vote on all federal legislation and the national budget',
      'Introduce bills on taxes, healthcare, immigration, defense',
      'Oversee federal agencies (FBI, EPA, VA, etc.)',
      'Approve or block presidential appointments (with the Senate)',
      'Represent ~760,000 constituents each',
    ],
    lookFor:
      "The best congressional reps are relentless communicators who actually hold town halls and respond to constituent mail — not just during election season. Look for someone with policy depth in areas that matter to your district (agriculture for rural areas, tech for urban, military for base-adjacent communities). They should be able to explain their votes clearly, not hide behind party talking points. Prior experience in state or local government is a strong signal — they understand how federal dollars actually land in communities. Beware of candidates who've never lived in the district or who treat the seat as a stepping stone to cable news.",
    sources: [
      { label: 'U.S. House of Representatives', url: 'https://www.house.gov/' },
      { label: 'Congress.gov — Track bills & votes', url: 'https://www.congress.gov/' },
      { label: 'GovTrack — Voting records & report cards', url: 'https://www.govtrack.us/' },
      { label: 'OpenSecrets — Campaign finance data', url: 'https://www.opensecrets.org/' },
      {
        label: 'Contact your representative',
        url: 'https://www.house.gov/representatives/find-your-representative',
      },
      { label: 'Congressional Research Service reports', url: 'https://crsreports.congress.gov/' },
    ],
  },
  senate: {
    title: 'Senate District',
    color: '#3B82F6',
    about:
      "Your state senator is one of the most powerful people in your political life — and most people can't name theirs. State senators shape the laws you actually live under every day: property taxes, school funding formulas, abortion access, gun regulations, Medicaid expansion, criminal sentencing, and zoning. In Texas, the 31-member Senate has an outsized role because the Lt. Governor (who presides over it) controls which bills even get a vote. A single state senator can block or advance legislation affecting millions.",
    powers: [
      'Set property tax rates and school funding formulas',
      "Confirm or reject the governor's appointments",
      'Draw congressional and legislative district maps (redistricting)',
      'Control state criminal law — sentencing, bail, marijuana policy',
      'Vote on state budget (~$300B in Texas biennially)',
    ],
    lookFor:
      "State senators need to be coalition-builders — in a small chamber of 31 people, one senator's ability to negotiate across the aisle determines whether your community gets funding or gets ignored. Look for candidates who understand budgets and can explain where state money actually goes (most people are shocked to learn how much goes to Medicaid and education). Deep local roots matter: a senator who grew up in the district and sends their kids to local schools will fight differently than one parachuted in by a party. Watch out for candidates who run on national culture-war issues but have no plan for property taxes or school funding — those are the bills that actually hit your kitchen table.",
    sources: [
      { label: 'OpenStates — State legislation tracker', url: 'https://openstates.org/' },
      { label: 'National Conference of State Legislatures', url: 'https://www.ncsl.org/' },
      { label: 'Ballotpedia — State senate races', url: 'https://ballotpedia.org/State_senate' },
      { label: 'FollowTheMoney — State campaign finance', url: 'https://www.followthemoney.org/' },
      { label: 'LegiScan — Bill tracking across all states', url: 'https://legiscan.com/' },
      { label: 'Texas Legislature Online', url: 'https://capitol.texas.gov/' },
    ],
  },
  house: {
    title: 'House District',
    color: '#10B981',
    about:
      "State house representatives are the closest elected officials to your daily life. They serve smaller districts than senators, which means they're more accessible and more directly accountable to neighborhoods. The state house is where most legislation originates — from school voucher programs to driver's license rules to local business regulations. Because there are more of them (150 in Texas), individual reps often specialize in specific policy areas and build coalitions to pass bills. They're also frequently the stepping stone to higher office, so engaging with them early builds lasting political relationships.",
    powers: [
      'Originate all state revenue and tax bills',
      'Write laws on education, transportation, and local government',
      'Approve or reject the state budget',
      'Investigate state agencies and hold hearings',
      'Propose amendments to the state constitution',
    ],
    lookFor:
      "Because state house districts are small, your rep should genuinely know the neighborhoods they serve — the flooding problems, the school overcrowding, the road that's been broken for three years. The best house members are workhorses, not show horses: they show up to committee hearings, read the bills, and do the unglamorous work of getting amendments passed. Look for someone who can name specific local problems and has a concrete plan, not just slogans. Community involvement before running for office (PTA, city council, neighborhood association, local business) is a strong signal they'll actually do the job. This is an entry-level political position — enthusiasm and hustle matter more than a polished resume.",
    sources: [
      { label: 'OpenStates — State legislation tracker', url: 'https://openstates.org/' },
      { label: 'National Conference of State Legislatures', url: 'https://www.ncsl.org/' },
      { label: 'Ballotpedia — State house races', url: 'https://ballotpedia.org/State_house' },
      { label: 'FollowTheMoney — State campaign finance', url: 'https://www.followthemoney.org/' },
      { label: 'LegiScan — Bill tracking across all states', url: 'https://legiscan.com/' },
      { label: 'Texas Legislature Online', url: 'https://capitol.texas.gov/' },
    ],
  },
  school: {
    title: 'School District',
    color: '#F59E0B',
    about:
      'School board elections are the most underrated elections in America. School board members control curriculum standards, teacher pay, school budgets (often hundreds of millions of dollars), bond measures for new facilities, and policies on everything from book access to bathroom policies to school safety. Turnout is typically under 10%, which means a few hundred votes can swing an election — and with it, the direction of education for thousands of kids. These races have become increasingly high-stakes as national culture-war issues play out at the local level.',
    powers: [
      'Set curriculum standards and approve textbooks',
      'Hire and fire the superintendent',
      'Control the district budget and teacher compensation',
      'Approve bond measures for school construction',
      'Set policies on student safety, inclusion, and discipline',
    ],
    lookFor:
      "School board members should care about kids first and politics second. The best ones have direct experience with the district — as parents, teachers, administrators, or community volunteers. They should be able to read a budget (school districts manage hundreds of millions) and ask hard questions of the superintendent without grandstanding. Be wary of candidates who are running primarily to fight national culture-war battles at the local level — while those issues matter, a board member who can't discuss per-pupil spending, teacher retention, or facility maintenance is going to be ineffective at the actual job. The ideal school board member is boring in the best way: shows up to every meeting, reads every packet, and makes decisions based on data and community input.",
    sources: [
      { label: 'National School Boards Association', url: 'https://www.nsba.org/' },
      { label: 'GreatSchools — School ratings & reviews', url: 'https://www.greatschools.org/' },
      { label: 'SchoolDigger — Rankings & comparisons', url: 'https://www.schooldigger.com/' },
      {
        label: 'Ballotpedia — School board elections',
        url: 'https://ballotpedia.org/School_board',
      },
      { label: 'NCES School District Search', url: 'https://nces.ed.gov/ccd/districtsearch/' },
      { label: 'Texas Education Agency', url: 'https://tea.texas.gov/' },
    ],
  },
};

/**
 * Parse district label to extract state and number
 * Label examples: "TX's_10th_Congressional_District", "TX's_21st_Senate_District"
 */
function parseLabel(label, districtType, stateParam) {
  const decoded = decodeURIComponent(label).replace(/_/g, ' ');

  // Congressional: state is a separate param
  if (districtType === 'congressional' && stateParam) {
    return { state: stateParam.toUpperCase(), num: decoded.match(/(\d+)/)?.[1] };
  }

  // School: use the full name as number/identifier
  if (districtType === 'school') {
    return { state: null, num: decoded };
  }

  // Senate/House: extract state and number from label
  const state = decoded.match(/^([A-Z]{2})/)?.[1];
  const num = decoded.match(/(\d+)/)?.[1];
  return { state, num };
}

export default function DistrictDetail({ districtType }) {
  const { state: stateParam, label } = useParams();
  const { state, num } = parseLabel(label, districtType, stateParam);
  const [mapInteractive, setMapInteractive] = useState(false);

  const config = DISTRICT_CONFIG[districtType] || {};

  const { data, isLoading, error } = useQuery({
    queryKey: ['district-detail', districtType, state, num],
    queryFn: () => civicAPI.getDistrictDetail(districtType, state, num),
    enabled: !!state && !!num,
  });

  useSEO({
    title: `${state}'s ${num} ${config.title}`,
    description: `Learn about ${state}'s ${num}${config.title} — district map, elections, and official resources.`,
  });

  const stateName = { TX: 'Texas', CA: 'California', NY: 'New York' }[state] || state;
  const ordinal = ['1', '21', '31', '41', '51', '61', '71', '81', '91'].includes(num)
    ? 'st'
    : ['2', '22', '32', '42', '52', '62', '72', '82', '92'].includes(num)
      ? 'nd'
      : ['3', '23', '33', '43', '53', '63', '73', '83', '93'].includes(num)
        ? 'rd'
        : 'th';

  const districtName =
    districtType === 'school' ? num : `${stateName}'s ${num}${ordinal} ${config.title}`;

  const wikiSlug = {
    congressional: `${stateName}'s_${num}${ordinal}_congressional_district`,
    senate: `${stateName}'s_${num}${ordinal}_Senate_district`,
    house: `${stateName}'s_${num}${ordinal}_House_of_Representatives_district`,
    school: num.replace(/ /g, '_'),
  }[districtType];

  usePageTitle(districtName);

  const { feature, elections } = data || {};

  return (
    <DetailPageLayout
      title={districtName}
      breadcrumbs={[{ label: 'Vote', url: '/vote' }, { label: districtName }]}
      isLoading={isLoading}
      error={error}
      hero={
        data && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: config.color }}
              />
              <p className="text-neutral-600">
                {config.title} · {stateName}
              </p>
            </div>
            <a
              href={`https://en.wikipedia.org/wiki/${encodeURIComponent(wikiSlug)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-neutral-600 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition-colors"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.09 13.119c-.936 1.932-2.217 4.548-2.853 5.728-.616 1.074-1.127.931-1.532.029-1.406-3.321-4.293-9.144-5.651-12.409-.251-.601-.441-.987-.619-1.139-.181-.15-.554-.24-1.122-.271C.103 5.033 0 4.982 0 4.898v-.455l.052-.045c.924-.005 5.401 0 5.401 0l.051.045v.434c0 .119-.075.176-.225.176l-.564.031c-.485.029-.727.164-.727.407 0 .2.11.566.328 1.085l3.937 8.911c.023-.045 1.966-4.111 1.966-4.111s-.455-.998-.646-1.467c-.652-1.599-1.108-2.726-1.364-3.378-.186-.473-.312-.727-.378-.763-.07-.04-.378-.07-.93-.093-.116 0-.175-.058-.175-.176v-.455l.046-.045c.08 0 1.898 0 4.107.002l.046.045v.455c0 .118-.059.176-.176.176-.467.013-.723.053-.768.121s-.023.282.064.622c.511 1.545 1.199 3.368 1.199 3.368l1.228-2.571c.171-.364.257-.669.257-.911 0-.387-.17-.6-.511-.641l-.656-.07c-.117 0-.176-.058-.176-.176v-.455l.046-.045h3.685l.046.045v.455c0 .118-.059.176-.176.176-.345.02-.627.088-.849.202-.215.117-.461.39-.738.82L12.09 13.119zm2.642-1.04c1.678 3.601 2.851 6.202 3.515 7.786.421.978.767 1.028 1.139.06l3.467-8.942c.237-.552.357-.877.357-.977 0-.291-.236-.448-.711-.469l-.59-.031c-.117 0-.176-.058-.176-.176v-.455l.046-.045c.923-.005 4.387 0 4.387 0l.046.045v.455c0 .118-.059.176-.176.176-.527.02-.932.14-1.217.358-.279.215-.557.597-.833 1.145l-4.7 11.381c-.117.283-.235.424-.352.424s-.233-.141-.352-.424L14.042 11.12l-.058-.091c-.015-.02.058.091 0 0l.75.05z" />
              </svg>
              Wikipedia
            </a>
          </div>
        )
      }
      sections={[
        {
          title: 'District Map',
          content: data && (
            <div className="space-y-4">
              <div
                className="relative"
                style={{ height: '400px' }}
                onClick={() => !mapInteractive && setMapInteractive(true)}
              >
                {!mapInteractive && (
                  <div className="absolute inset-0 z-10 cursor-pointer flex items-end justify-center pb-12">
                    <span className="bg-white/90 text-neutral-600 text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
                      Click to interact with map
                    </span>
                  </div>
                )}
                <PlaceMap
                  key={mapInteractive ? 'interactive' : 'frozen'}
                  districtData={{ [districtType]: [feature] }}
                  height="100%"
                  frozen={!mapInteractive}
                />
              </div>
              {districtType !== 'school' && (
                <p className="text-xs text-neutral-400 leading-relaxed">
                  <span className="font-medium text-neutral-500">
                    Why does my district look like this?
                  </span>{' '}
                  District boundaries are redrawn every 10 years after the census. The process —
                  called redistricting — is often controlled by state legislators, which can lead to
                  gerrymandering: drawing lines to favor one party. Oddly shaped districts are a
                  common sign.{' '}
                  <a
                    href="https://www.brennancenter.org/topics/voting-elections/redistricting"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-700 underline"
                  >
                    Learn how to fight gerrymandering
                  </a>
                </p>
              )}
            </div>
          ),
        },
        ...(config.about
          ? [
              {
                title: 'Why this seat matters',
                content: (
                  <div className="space-y-4">
                    <p className="text-sm text-neutral-700 leading-relaxed">{config.about}</p>
                    {config.powers && (
                      <div>
                        <h4 className="text-sm font-semibold text-neutral-900 mb-2">Key powers</h4>
                        <ul className="space-y-1.5">
                          {config.powers.map((power) => (
                            <li
                              key={power}
                              className="flex items-start gap-2 text-sm text-neutral-600"
                            >
                              <span
                                className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0"
                                style={{ backgroundColor: config.color }}
                              />
                              {power}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {config.lookFor && (
                      <div className="pt-4 border-t border-neutral-100">
                        <h4 className="text-sm font-semibold text-neutral-900 mb-2">
                          What to look for in a candidate
                        </h4>
                        <p className="text-sm text-neutral-700 leading-relaxed">{config.lookFor}</p>
                      </div>
                    )}
                    {config.sources && (
                      <div className="pt-4 border-t border-neutral-100">
                        <h4 className="text-sm font-semibold text-neutral-900 mb-3">
                          Sources & resources
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {config.sources.map((source) => (
                            <a
                              key={source}
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-800 hover:underline transition-colors"
                            >
                              <svg
                                className="w-3.5 h-3.5 shrink-0"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                              {source.label}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
            ]
          : []),
        ...(data?.representative
          ? [
              {
                title: 'Your Representative',
                content: (
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-14 h-14 bg-neutral-100 rounded-full shrink-0 flex items-center justify-center text-2xl">
                        {data.representative.partyShort === 'R'
                          ? '🔴'
                          : data.representative.partyShort === 'D'
                            ? '🔵'
                            : '⚪'}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-neutral-900">
                          {data.representative.name}
                        </h3>
                        <p className="text-sm text-neutral-600">
                          {data.representative.party} · {stateName} District {num}
                        </p>
                        {data.representative.phone && (
                          <p className="text-sm text-neutral-500 mt-1">
                            <a
                              href={`tel:${data.representative.phone}`}
                              className="hover:text-primary-600"
                            >
                              {data.representative.phone}
                            </a>
                            {data.representative.office && ` · ${data.representative.office}`}
                          </p>
                        )}
                      </div>
                    </div>
                    {data.representative.committees &&
                      data.representative.committees.length > 0 && (
                        <div className="pt-4 border-t border-neutral-100">
                          <h4 className="text-sm font-medium text-neutral-700 mb-2">
                            Committee Assignments
                          </h4>
                          <div className="flex flex-wrap gap-1.5">
                            {data.representative.committees.map((committee) => {
                              const url = getCommitteeUrl(committee);
                              return url ? (
                                <a
                                  key={committee}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs bg-neutral-100 text-primary-600 hover:bg-primary-50 hover:text-primary-700 px-2.5 py-1 rounded-full transition-colors"
                                >
                                  {committee}
                                </a>
                              ) : (
                                <span
                                  key={committee}
                                  className="text-xs bg-neutral-100 text-neutral-700 px-2.5 py-1 rounded-full"
                                >
                                  {committee}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    {data.representative.citation && (
                      <p className="text-xs text-neutral-400 mt-4">
                        Source: {data.representative.citation.author}. "
                        {data.representative.citation.title}."
                        <em> {data.representative.citation.publisher}</em>,{' '}
                        <a
                          href={data.representative.citation.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-neutral-600"
                        >
                          {data.representative.citation.url}
                        </a>
                        . Accessed {data.representative.citation.accessDate}.
                      </p>
                    )}
                  </div>
                ),
              },
            ]
          : [
              {
                title: 'Representative',
                content: (
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-neutral-100 rounded-full shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-neutral-600">
                        {districtType === 'congressional'
                          ? 'Representative data could not be loaded. Visit house.gov for current information.'
                          : "Coming soon — we're integrating OpenStates API to show state legislator data."}
                      </p>
                    </div>
                  </div>
                ),
              },
            ]),
        ...(elections && elections.length > 0
          ? [
              {
                title: `Statewide Elections`,
                content: (
                  <div className="space-y-3">
                    {elections.map((election) => (
                      <div key={election.id} className="border border-neutral-200 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-neutral-900 mb-1">{election.name}</h4>
                            <p className="text-sm text-neutral-500">
                              {new Date(election.electionDate).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded shrink-0">
                            {election.electionType || 'General'}
                          </span>
                        </div>
                        <Link
                          to={`/elections/${election.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                          </svg>
                          Alert me about this election
                        </Link>
                      </div>
                    ))}
                  </div>
                ),
              },
            ]
          : []),
        ...(data?.legislation && data.legislation.length > 0
          ? [
              {
                title: 'Recent Legislation',
                content: (
                  <div className="border border-neutral-200 rounded-lg overflow-hidden divide-y divide-neutral-100">
                    {data.legislation.map((bill) => (
                      <a
                        key={bill}
                        href={bill.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-5 py-3.5 hover:bg-neutral-50 transition-colors"
                      >
                        <div className="flex items-baseline gap-2">
                          <span className="text-xs font-mono text-primary-600 shrink-0">
                            {bill.billNumber}
                          </span>
                          <h4 className="text-sm text-neutral-900 line-clamp-1">{bill.title}</h4>
                        </div>
                      </a>
                    ))}
                    <div className="px-5 py-3 border-t border-neutral-100 bg-neutral-50">
                      <a
                        href="https://www.congress.gov/most-viewed-bills"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                      >
                        View all on Congress.gov
                      </a>
                    </div>
                  </div>
                ),
              },
            ]
          : []),
        ...(data?.news && data.news.length > 0
          ? [
              {
                title: 'In the News',
                content: (
                  <div className="border border-neutral-200 rounded-lg overflow-hidden divide-y divide-neutral-100">
                    {data.news.slice(0, 3).map((article) => (
                      <a
                        key={article}
                        href={article.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block px-5 py-3.5 hover:bg-neutral-50 transition-colors"
                      >
                        <h4 className="text-sm font-medium text-neutral-900 mb-1 line-clamp-2">
                          {article.title}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-neutral-400">
                          {article.source && <span>{article.source}</span>}
                          {article.source && article.publishedAt && <span>·</span>}
                          {article.publishedAt && (
                            <span>
                              {new Date(article.publishedAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </span>
                          )}
                        </div>
                      </a>
                    ))}
                    {data.newsSearchUrl && (
                      <div className="px-5 py-3 border-t border-neutral-100 bg-neutral-50">
                        <a
                          href={data.newsSearchUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          More on Google News
                        </a>
                      </div>
                    )}
                  </div>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}
